<template>
  <div id="app">
    <l-map
      :zoom="zoom"
      :center="center"
      style="height: 500px; width: 100%; z-index: 2;"
      ref="map"
      @ready="onMapReady"
    >
      <l-tile-layer :url="url" :attribution="attribution" />
      <l-polyline
        v-if="hasGeoPoints"
        :lat-lngs="localGeoPoints"
        :color="polylineStyle.color"
        :weight="polylineStyle.weight"
      />

      <l-marker v-if="markerStart" :lat-lng="markerStart" :icon="defaultIcon">
        <l-popup>
          {{ $t('routeMap.start') }}
        </l-popup>
      </l-marker>

      <l-marker v-if="markerEnd" :lat-lng="markerEnd" :icon="personIcon">
        <l-popup>
          {{ $t('routeMap.locationPatient') }}
        </l-popup>
      </l-marker>
    </l-map>
    <div v-if="geoDistance" class="map-overlay">
      <div class="font-weight-light">
        {{ $t('src/components/training/trainingresults6mwt.distanceCovered') }}: {{ geoDistanceStr }}
      </div>
      <div class="font-weight-light" v-if="durationInSecByGeoPoints">
        {{ $t('routeMap.averageSpeed') }}: {{ averageSpeedStr }}
      </div>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LPolyline, LMarker, LPopup } from 'vue2-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-polylinedecorator'

const DEFAULT_ZOOM = 16
const POLYLINE_STYLE = {
  color: '#0482dc',
  weight: 2
}

export default {
  name: 'RouteMap',
  components: {
    LMap,
    LTileLayer,
    LPolyline,
    LMarker,
    LPopup
  },
  props: {
    geoLocationPoints: {
      type: Object,
      required: true
    },
    geoDistance: {
      type: Number,
      required: false
    }
  },
  data: () => ({
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    zoom: DEFAULT_ZOOM,
    localGeoPoints: [],
    center: [0, 0],
    polylineStyle: POLYLINE_STYLE,
    defaultIcon: L.icon({
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    personIcon: L.icon({
      iconUrl: require('@/assets/img/person.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    })
  }),
  computed: {
    hasGeoPoints() {
      return this.localGeoPoints.length > 0
    },
    markerStart() {
      return this.localGeoPoints[0] || null
    },
    markerEnd() {
      return this.localGeoPoints[this.localGeoPoints.length - 1] || null
    },
    geoDistanceStr() {
      if (!this.geoDistance) return '—'

      return this.geoDistance > 1000
        ? (this.geoDistance / 1000).toFixed(1) + ' ' + this.$t('unit.km')
        : this.geoDistance + ' ' + this.$t('unit.m')
    },
    averageSpeedStr() {
      if (!this.durationInSecByGeoPoints) return '—'

      const speed = this.geoDistance / this.durationInSecByGeoPoints
      return this.geoDistance > 1000
        ? `${(speed * 3.6).toFixed(1)} ${this.$t('unit.km/h')}`
        : `${speed.toFixed(1)} ${this.$t('unit.m/s')}`
    },
    durationInSecByGeoPoints() {
      if (!this.geoLocationPoints.timeValues?.length) return null

      const times = this.geoLocationPoints.timeValues
      return times[times.length - 1] - times[0]
    }
  },
  watch: {
    geoLocationPoints: {
      handler(newVal) {
        this.localGeoPoints = newVal.geoValues
        this.updateCenter()
        this.$nextTick(this.updatePolylineDecorator)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    updateCenter() {
      if (this.localGeoPoints.length > 0) {
        this.center = this.localGeoPoints[this.localGeoPoints.length - 1]
      }
    },
    onMapReady(mapInstance) {
      this.map = mapInstance
      this.updatePolylineDecorator()
    },
    updatePolylineDecorator() {
      if (!this.map || !this.hasGeoPoints) return

      if (this.polylineDecorator) {
        this.$refs.map.mapObject.removeLayer(this.polylineDecorator)
      }
      this.polylineDecorator = L.polylineDecorator(this.localGeoPoints, {
        patterns: [
          {
            offset: 0,
            repeat: 60,
            symbol: L.Symbol.arrowHead({
              pixelSize: 10,
              polygon: true,
              pathOptions: {
                stroke: true,
                color: POLYLINE_STYLE.color,
                fillColor: POLYLINE_STYLE.color,
                fillOpacity: 1
              }
            })
          }
        ]
      }).addTo(this.$refs.map.mapObject)
    }
  }
}
</script>

<style>
.map-overlay {
  position: absolute;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  bottom: 5px;
  left: 5px;
  z-index: 3;
  padding: 5px;
  background-color: rgb(255 255 255 / 70%);
}

.leaflet-attribution-flag {
  display: none !important;
}
</style>
